import React, { useState, useEffect } from 'react';
import { 
  Box, 
  VStack, 
  Card, 
  CardHeader, 
  CardBody, 
  HStack, 
  Text,
  Button,
  SimpleGrid,
  Input,
  FormLabel,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Badge,
  Wrap,
  WrapItem,
  Image
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useReservaFilters } from '../../hooks/useReservaFilters';
import ReservaFilters from '../../components/ReservaFilters';
import FilterBadge from '../../components/FilterBadge';
import { getDeviceHardwareEmoji } from '../../utils/deviceHelpers';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';

const AdminStats = () => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const [expandedCards, setExpandedCards] = useState({
    reservas: true,
    clientes: false,
    conductores: false
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [mostrarSelectores, setMostrarSelectores] = useState(false);
  const [mostrarFiltrosClientes, setMostrarFiltrosClientes] = useState(false);
  const [mostrarFiltrosConductores, setMostrarFiltrosConductores] = useState(false);
  const [mostrarFiltrosIdiomas, setMostrarFiltrosIdiomas] = useState(false);
  
  // Función para expandir/colapsar todos los filtros
  const toggleTodosFiltros = () => {
    const todosExpandidos = mostrarSelectores && mostrarFiltros && mostrarFiltrosClientes && mostrarFiltrosConductores && mostrarFiltrosIdiomas;
    const nuevoEstado = !todosExpandidos;
    setMostrarSelectores(nuevoEstado);
    setMostrarFiltros(nuevoEstado);
    setMostrarFiltrosClientes(nuevoEstado);
    setMostrarFiltrosConductores(nuevoEstado);
    setMostrarFiltrosIdiomas(nuevoEstado);
  };
  
  // Estados para selectores temporales
  const [periodoActivo, setPeriodoActivo] = useState('todos');
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);
  const [fechaInicio, setFechaInicio] = useState('');
  
  // Estados para datos de reservas
  const [reservas, setReservas] = useState([]);
  const [dispositivos, setDispositivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado para filtros de clientes (todos seleccionados por defecto)
  const [clientesActivos, setClientesActivos] = useState({});
  
  // Estado para filtros de conductores (todos seleccionados por defecto)
  const [conductoresActivos, setConductoresActivos] = useState({});
  
  // Estado para filtros de idiomas (todos seleccionados por defecto)
  const [idiomasActivos, setIdiomasActivos] = useState({});

  // Hook personalizado para manejo de filtros de reservas
  const {
    estadosActivos,
    subEstadosActivos,
    toggleEstado,
    toggleSubEstado
  } = useReservaFilters(
    {
      'activo': true,
      'inactivo': true,
      'cancelado': true
    },
    {
      'bolsa': true,
      'asignada_espera': true,
      'en_curso': true,
      'completado_sin_confirmar': true,
      'pendiente_devolucion': true,
      'pendiente_pago': true,
      'finalizado': true,
      'fallido': true,
      'cancelado_cliente': true,
      'cancelado_administrador': true,
      'cancelado_automatico': true
    }
  );

  const toggleCard = (cardName) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardName]: !prev[cardName]
    }));
  };

  // Calcular fecha de inicio según periodo temporal
  useEffect(() => {
    if (periodoActivo === 'todos') {
      setFechaInicio('');
      return;
    }

    const fechaFinDate = new Date(fechaFin);
    const fechaInicioDate = new Date(fechaFinDate);

    switch (periodoActivo) {
      case '24horas':
        fechaInicioDate.setDate(fechaInicioDate.getDate() - 1);
        break;
      case 'semana':
        fechaInicioDate.setDate(fechaInicioDate.getDate() - 7);
        break;
      case 'mes':
        fechaInicioDate.setDate(fechaInicioDate.getDate() - 30);
        break;
      case 'trimestre':
        fechaInicioDate.setDate(fechaInicioDate.getDate() - 90);
        break;
      case 'anio':
        fechaInicioDate.setDate(fechaInicioDate.getDate() - 365);
        break;
      default:
        break;
    }

    setFechaInicio(fechaInicioDate.toISOString().split('T')[0]);
  }, [periodoActivo, fechaFin]);

  // Fetch reservas desde API
  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const fetchReservas = async () => {
      try {
        setLoading(true);
        const baseURL = process.env.REACT_APP_API_BASE_URL;
        
        const response = await fetch(`${baseURL}/api/reservas/`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status}`);
        }
        
        const data = await response.json();
        setReservas(data);
        
        // Extraer lista única de clientes y activar todos por defecto
        const clientesUnicos = {};
        data.forEach(reserva => {
          if (reserva.cliente_email) {
            const clienteKey = reserva.cliente_email;
            if (!clientesUnicos[clienteKey]) {
              clientesUnicos[clienteKey] = {
                email: reserva.cliente_email,
                nombre: reserva.cliente_nombre || '',
                apellidos: reserva.cliente_apellidos || '',
                activo: true
              };
            }
          }
        });
        
        // Inicializar filtros de clientes (todos activos)
        const clientesActivosInicial = {};
        Object.keys(clientesUnicos).forEach(key => {
          clientesActivosInicial[key] = true;
        });
        setClientesActivos(clientesActivosInicial);
        
        // Extraer lista única de conductores y activar todos por defecto
        const conductoresUnicos = {};
        data.forEach(reserva => {
          if (reserva.conductor_email) {
            const conductorKey = reserva.conductor_email;
            if (!conductoresUnicos[conductorKey]) {
              conductoresUnicos[conductorKey] = {
                email: reserva.conductor_email,
                nombre: reserva.conductor_nombre || '',
                apellidos: reserva.conductor_apellidos || '',
                activo: true
              };
            }
          }
        });
        
        // Inicializar filtros de conductores (todos activos)
        const conductoresActivosInicial = {};
        Object.keys(conductoresUnicos).forEach(key => {
          conductoresActivosInicial[key] = true;
        });
        setConductoresActivos(conductoresActivosInicial);
        
        // Extraer lista única de idiomas y activar todos por defecto
        // Incluir todos los idiomas soportados aunque no haya reservas con ellos
        const idiomasSoportados = ['ES', 'EN', 'DE', 'FR', 'IT', 'RU', 'CA'];
        const idiomasUnicos = {};
        
        // Añadir todos los idiomas soportados
        idiomasSoportados.forEach(idioma => {
          idiomasUnicos[idioma] = true;
        });
        
        // También incluir cualquier otro idioma que esté en los datos
        data.forEach(reserva => {
          if (reserva.idioma) {
            const idioma = reserva.idioma.toUpperCase();
            if (!idiomasUnicos[idioma]) {
              idiomasUnicos[idioma] = true;
            }
          }
        });
        
        console.log('[AdminStats] Idiomas encontrados:', Object.keys(idiomasUnicos));
        
        // Inicializar filtros de idiomas (todos activos)
        const idiomasActivosInicial = {};
        Object.keys(idiomasUnicos).forEach(key => {
          idiomasActivosInicial[key] = true;
        });
        setIdiomasActivos(idiomasActivosInicial);
        
        // Cargar dispositivos desde todos los usuarios
        try {
          // Obtener todos los dispositivos con coordenadas
          const dispositivosResponse = await fetch(`${baseURL}/api/dispositivos/mapa/todos`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (dispositivosResponse.ok) {
            const dispositivosData = await dispositivosResponse.json();
            // Extraer solo los datos del dispositivo de cada entrada
            const dispositivos = dispositivosData.map(item => ({
              id: item.id,
              user_id: item.user_id,
              platform: item.platform,
              user_agent: item.user_agent,
              custom_device_id: item.custom_device_id
            }));
            setDispositivos(dispositivos);
            console.log('[AdminStats] Dispositivos cargados:', dispositivos.length);
          } else {
            console.error('[AdminStats] Error al cargar dispositivos:', dispositivosResponse.status);
          }
        } catch (dispError) {
          console.error('[AdminStats] Error al cargar dispositivos:', dispError);
        }
        
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    
    fetchReservas();
  }, [accessToken]);

  // Filtrar reservas según filtros activos y periodo temporal
  const reservasFiltradas = reservas.filter(reserva => {
    // Filtro por estado
    if (!estadosActivos[reserva.estado]) return false;
    
    // Filtro por sub-estado
    if (!subEstadosActivos[reserva.sub_estado]) return false;
    
    // Filtro por cliente
    if (reserva.cliente_email && !clientesActivos[reserva.cliente_email]) return false;
    
    // Filtro por conductor
    if (reserva.conductor_email && !conductoresActivos[reserva.conductor_email]) return false;
    
    // Filtro por idioma
    if (reserva.idioma && !idiomasActivos[reserva.idioma.toUpperCase()]) return false;
    
    // Filtro temporal
    if (periodoActivo !== 'todos') {
      const fechaInicio = new Date(reserva.fecha_hora_inicio);
      const fechaFinDate = new Date(fechaFin);
      fechaFinDate.setHours(23, 59, 59, 999); // Fin del día
      
      let fechaInicioRango = new Date(fechaFinDate);
      
      switch (periodoActivo) {
        case '24horas':
          fechaInicioRango.setDate(fechaInicioRango.getDate() - 1);
          break;
        case 'semana':
          fechaInicioRango.setDate(fechaInicioRango.getDate() - 7);
          break;
        case 'mes':
          fechaInicioRango.setDate(fechaInicioRango.getDate() - 30);
          break;
        case 'trimestre':
          fechaInicioRango.setDate(fechaInicioRango.getDate() - 90);
          break;
        case 'anio':
          fechaInicioRango.setDate(fechaInicioRango.getDate() - 365);
          break;
        default:
          break;
      }
      
      if (fechaInicio < fechaInicioRango || fechaInicio > fechaFinDate) {
        return false;
      }
    }
    
    return true;
  });

  // Calcular estadísticas
  const calcularEstadisticas = () => {
    const total = reservasFiltradas.length;
    
    // Conteo por estado
    const porEstado = reservasFiltradas.reduce((acc, r) => {
      acc[r.estado] = (acc[r.estado] || 0) + 1;
      return acc;
    }, {});
    
    // Conteo por sub-estado
    const porSubEstado = reservasFiltradas.reduce((acc, r) => {
      acc[r.sub_estado] = (acc[r.sub_estado] || 0) + 1;
      return acc;
    }, {});
    
    // Ingresos totales
    const ingresosTotales = reservasFiltradas.reduce((sum, r) => {
      return sum + (parseFloat(r.importe_reserva) || 0);
    }, 0);
    
    // Ingresos por adjudicaciones
    const ingresosAdjudicacion = reservasFiltradas.reduce((sum, r) => {
      return sum + (parseFloat(r.importe_adjudicacion) || 0);
    }, 0);
    
    // Promedio por reserva
    const promedioReserva = total > 0 ? ingresosTotales / total : 0;
    
    // Conteo por tipo de servicio
    const porTipoServicio = reservasFiltradas.reduce((acc, r) => {
      const tipo = r.tipo_servicio || 'desconocido';
      acc[tipo] = (acc[tipo] || 0) + 1;
      return acc;
    }, {});
    
    // Conteo por idioma
    const porIdioma = reservasFiltradas.reduce((acc, r) => {
      const idioma = r.idioma || 'ES'; // Por defecto ES si no está definido
      acc[idioma] = (acc[idioma] || 0) + 1;
      return acc;
    }, {});

    // Conteo por tipo de dispositivo
    const porTipoDispositivo = reservasFiltradas.reduce((acc, r) => {
      // Obtener el dispositivo asociado a la reserva
      const dispositivo = dispositivos.find(d => d.id === r.dispositivo_cliente);
      
      if (dispositivo) {
        const emoji = getDeviceHardwareEmoji(dispositivo.platform, dispositivo.user_agent);
        let tipo = 'Desconocido 👤';
        
        if (emoji === '💻') tipo = 'PC 💻';
        else if (emoji === '🤖') tipo = 'Android 🤖';
        else if (emoji === '🍎') tipo = 'Apple 🍎';
        
        acc[tipo] = (acc[tipo] || 0) + 1;
      } else {
        acc['Sin datos ❓'] = (acc['Sin datos ❓'] || 0) + 1;
      }
      
      return acc;
    }, {});
    
    // Reservas por día según periodo temporal
    const fechaFinDate = new Date(fechaFin);
    const reservasPorDia = [];
    let numDias = 7; // Por defecto
    
    // Determinar número de días según periodo
    switch (periodoActivo) {
      case '24horas':
        numDias = 1;
        break;
      case 'semana':
        numDias = 7;
        break;
      case 'mes':
        numDias = 30;
        break;
      case 'trimestre':
        numDias = 90;
        break;
      case 'anio':
        numDias = 365;
        break;
      case 'todos':
        // Para "todos", calcular días desde la reserva más antigua hasta fechaFin
        if (reservasFiltradas.length > 0) {
          const fechasMasAntigua = reservasFiltradas
            .map(r => new Date(r.fecha_hora_inicio))
            .sort((a, b) => a - b)[0];
          numDias = Math.ceil((fechaFinDate - fechasMasAntigua) / (1000 * 60 * 60 * 24)) + 1;
        } else {
          numDias = 30; // Default si no hay datos
        }
        break;
      default:
        numDias = 7;
    }
    
    // Generar datos para cada día
    for (let i = numDias - 1; i >= 0; i--) {
      const fecha = new Date(fechaFinDate);
      fecha.setDate(fecha.getDate() - i);
      const fechaStr = fecha.toISOString().split('T')[0];
      
      const count = reservasFiltradas.filter(r => {
        const fechaReserva = new Date(r.fecha_hora_inicio).toISOString().split('T')[0];
        return fechaReserva === fechaStr;
      }).length;
      
      // Formato de fecha según número de días
      let formatoFecha;
      if (numDias <= 7) {
        formatoFecha = fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
      } else if (numDias <= 31) {
        formatoFecha = fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
      } else if (numDias <= 90) {
        formatoFecha = fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
      } else {
        // Para períodos largos, mostrar solo mes/año
        formatoFecha = fecha.toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
      }
      
      reservasPorDia.push({
        fecha: formatoFecha,
        cantidad: count
      });
    }
    
    // Conteo por rangos de precio
    const rangosPrecios = {
      '0 a 5 €': 0,
      '5 a 10 €': 0,
      '10 a 15 €': 0,
      '15 a 20 €': 0,
      '20 a 30 €': 0,
      '30 a 40 €': 0,
      '40 a 50 €': 0,
      'Más de 50 €': 0
    };
    
    reservasFiltradas.forEach(r => {
      const precio = parseFloat(r.precio_aproximado) || 0;
      if (precio < 5) {
        rangosPrecios['0 a 5 €']++;
      } else if (precio < 10) {
        rangosPrecios['5 a 10 €']++;
      } else if (precio < 15) {
        rangosPrecios['10 a 15 €']++;
      } else if (precio < 20) {
        rangosPrecios['15 a 20 €']++;
      } else if (precio < 30) {
        rangosPrecios['20 a 30 €']++;
      } else if (precio < 40) {
        rangosPrecios['30 a 40 €']++;
      } else if (precio < 50) {
        rangosPrecios['40 a 50 €']++;
      } else {
        rangosPrecios['Más de 50 €']++;
      }
    });
    
    const reservasPorPrecio = Object.entries(rangosPrecios).map(([rango, cantidad]) => ({
      rango,
      cantidad
    }));
    
    // Conteo por código postal de origen
    const porCodigoPostal = reservasFiltradas.reduce((acc, r) => {
      let codigoPostal = 'Sin código';
      if (r.origen_direccion) {
        // Extraer código postal de 5 dígitos de la dirección
        const match = r.origen_direccion.match(/\b\d{5}\b/);
        if (match) {
          codigoPostal = match[0];
        }
      }
      acc[codigoPostal] = (acc[codigoPostal] || 0) + 1;
      return acc;
    }, {});
    
    const reservasPorCodigoPostal = Object.entries(porCodigoPostal)
      .map(([codigo, cantidad]) => ({
        codigo,
        cantidad
      }))
      .sort((a, b) => b.cantidad - a.cantidad); // Ordenar por cantidad descendente
    
    // Conteo por código postal de destino
    const porCodigoPostalDestino = reservasFiltradas.reduce((acc, r) => {
      let codigoPostal = 'Sin código';
      if (r.destino_direccion) {
        // Extraer código postal de 5 dígitos de la dirección
        const match = r.destino_direccion.match(/\b\d{5}\b/);
        if (match) {
          codigoPostal = match[0];
        }
      }
      acc[codigoPostal] = (acc[codigoPostal] || 0) + 1;
      return acc;
    }, {});
    
    const reservasPorCodigoPostalDestino = Object.entries(porCodigoPostalDestino)
      .map(([codigo, cantidad]) => ({
        codigo,
        cantidad
      }))
      .sort((a, b) => b.cantidad - a.cantidad); // Ordenar por cantidad descendente
    
    // Estadísticas de clientes
    const clientesUnicos = new Set(reservasFiltradas.map(r => r.cliente_email).filter(Boolean));
    const totalClientes = clientesUnicos.size;
    const reservasPorCliente = totalClientes > 0 ? (reservasFiltradas.length / totalClientes).toFixed(2) : 0;
    
    // Clientes con más reservas
    const clientesConReservas = reservasFiltradas.reduce((acc, r) => {
      if (r.cliente_email) {
        if (!acc[r.cliente_email]) {
          acc[r.cliente_email] = {
            email: r.cliente_email,
            nombre: r.cliente_nombre || '',
            apellidos: r.cliente_apellidos || '',
            count: 0
          };
        }
        acc[r.cliente_email].count++;
      }
      return acc;
    }, {});
    
    const topClientes = Object.values(clientesConReservas)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    
    // Estadísticas de conductores
    const conductoresUnicos = new Set(
      reservasFiltradas
        .filter(r => r.conductor_email)
        .map(r => r.conductor_email)
    );
    const totalConductores = conductoresUnicos.size;
    const reservasConConductor = reservasFiltradas.filter(r => r.conductor_email).length;
    const reservasPorConductor = totalConductores > 0 ? (reservasConConductor / totalConductores).toFixed(2) : 0;
    
    // Conductores con más reservas
    const conductoresConReservas = reservasFiltradas
      .filter(r => r.conductor_email)
      .reduce((acc, r) => {
        if (!acc[r.conductor_email]) {
          acc[r.conductor_email] = {
            email: r.conductor_email,
            nombre: r.conductor_nombre || '',
            apellidos: r.conductor_apellidos || '',
            count: 0
          };
        }
        acc[r.conductor_email].count++;
        return acc;
      }, {});
    
    const topConductores = Object.values(conductoresConReservas)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    
    return {
      total,
      porEstado,
      porSubEstado,
      ingresosTotales,
      ingresosAdjudicacion,
      promedioReserva,
      porTipoServicio,
      porIdioma,
      porTipoDispositivo,
      reservasPorDia,
      reservasPorPrecio,
      reservasPorCodigoPostal,
      reservasPorCodigoPostalDestino,
      // Estadísticas de clientes
      totalClientes,
      reservasPorCliente,
      topClientes,
      // Estadísticas de conductores
      totalConductores,
      reservasConConductor,
      reservasPorConductor,
      topConductores
    };
  };

  const stats = calcularEstadisticas();

  // Colores para gráficos
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658'];
  
  // Colores para badges de clientes
  const CLIENTE_COLORS = ['blue', 'green', 'purple', 'orange', 'pink', 'teal', 'cyan', 'red'];

  // Obtener lista de clientes únicos con información
  const clientesUnicos = reservas.reduce((acc, reserva) => {
    if (reserva.cliente_email && !acc[reserva.cliente_email]) {
      acc[reserva.cliente_email] = {
        email: reserva.cliente_email,
        nombre: reserva.cliente_nombre || '',
        apellidos: reserva.cliente_apellidos || ''
      };
    }
    return acc;
  }, {});

  const toggleCliente = (email) => {
    setClientesActivos(prev => ({
      ...prev,
      [email]: !prev[email]
    }));
  };
  
  // Obtener lista de conductores únicos con información
  const conductoresUnicos = reservas.reduce((acc, reserva) => {
    if (reserva.conductor_email && !acc[reserva.conductor_email]) {
      acc[reserva.conductor_email] = {
        email: reserva.conductor_email,
        nombre: reserva.conductor_nombre || '',
        apellidos: reserva.conductor_apellidos || ''
      };
    }
    return acc;
  }, {});
  
  const toggleConductor = (email) => {
    setConductoresActivos(prev => ({
      ...prev,
      [email]: !prev[email]
    }));
  };
  
  // Obtener lista de idiomas únicos (siempre incluir los 7 idiomas soportados)
  const idiomasSoportados = ['ES', 'EN', 'DE', 'FR', 'IT', 'RU', 'CA'];
  // Solo mostrar los idiomas soportados oficialmente (ignorar otros que puedan venir en los datos)
  const idiomasUnicos = idiomasSoportados.sort();
  
  console.log('[AdminStats] Idiomas únicos para render:', idiomasUnicos);
  console.log('[AdminStats] Idiomas activos:', idiomasActivos);
  
  const toggleIdioma = (idioma) => {
    setIdiomasActivos(prev => ({
      ...prev,
      [idioma]: !prev[idioma]
    }));
  };
  
  // Función para obtener ruta de imagen de bandera según código ISO (solo idiomas de la web)
  const getBanderaPath = (codigoISO) => {
    const idiomaLower = codigoISO ? codigoISO.toLowerCase() : 'es';
    const iconosDisponibles = ['es', 'en', 'de', 'fr', 'it', 'ru', 'ca'];
    return iconosDisponibles.includes(idiomaLower)
      ? `/assets/icons/${idiomaLower}.png`
      : '/assets/icons/es.png';
  };

  // Datos para gráfico de estados
  const dataEstados = Object.entries(stats.porEstado).map(([key, value]) => ({
    name: key,
    value: value
  }));

  // Datos para gráfico de tipo de servicio
  const dataTipoServicio = Object.entries(stats.porTipoServicio).map(([key, value]) => ({
    name: key === 'inmediato' ? 'Inmediato' : 'Programado',
    value: value
  }));

  // Datos para gráfico de idiomas
  const dataIdiomas = Object.entries(stats.porIdioma).map(([key, value]) => ({
    name: key,
    value: value
  }));

  // Datos para gráfico de tipo de dispositivo
  const dataTipoDispositivo = Object.entries(stats.porTipoDispositivo).map(([key, value]) => ({
    name: key,
    value: value
  }));

  const renderResumenFiltros = () => {
    const filtros = [];

    // Filtros temporales
    filtros.push(`${fechaInicio || 'inicio'} - ${fechaFin}`);

    const estadosActivosList = Object.entries(estadosActivos).filter(([_, activo]) => activo);
    const estadosTotal = Object.keys(estadosActivos).length;

    const clientesActivosList = Object.entries(clientesActivos).filter(([_, activo]) => activo);
    const clientesTotal = Object.keys(clientesActivos).length;

    const conductoresActivosList = Object.entries(conductoresActivos).filter(([_, activo]) => activo);
    const conductoresTotal = Object.keys(conductoresActivos).length;

    const idiomasActivosList = Object.entries(idiomasActivos).filter(([_, activo]) => activo);
    const idiomasTotal = Object.keys(idiomasActivos).length;

    const todosSeleccionados =
      estadosTotal > 0 && estadosActivosList.length === estadosTotal &&
      clientesTotal > 0 && clientesActivosList.length === clientesTotal &&
      conductoresTotal > 0 && conductoresActivosList.length === conductoresTotal &&
      idiomasTotal > 0 && idiomasActivosList.length === idiomasTotal;

    if (todosSeleccionados) {
      filtros.push('Todos los datos');
    }

    if (estadosTotal > 0) {
      if (estadosActivosList.length === estadosTotal) {
        filtros.push('Estados: todos');
      } else if (estadosActivosList.length === 1) {
        filtros.push(`Estado: ${estadosActivosList[0][0]}`);
      } else if (estadosActivosList.length > 1) {
        filtros.push(`Estados: ${estadosActivosList.length}`);
      }
    }

    if (clientesTotal > 0) {
      if (clientesActivosList.length === clientesTotal) {
        filtros.push('Clientes: todos');
      } else if (clientesActivosList.length === 1) {
        const emailCliente = clientesActivosList[0][0];
        const cliente = clientesUnicos[emailCliente];
        if (cliente) {
          const nombreCompleto = `${cliente.nombre} ${cliente.apellidos}`.trim();
          filtros.push(nombreCompleto || cliente.email);
        }
      } else if (clientesActivosList.length > 1) {
        filtros.push(`Clientes: ${clientesActivosList.length}`);
      }
    }

    if (conductoresTotal > 0) {
      if (conductoresActivosList.length === conductoresTotal) {
        filtros.push('Conductores: todos');
      } else if (conductoresActivosList.length === 1) {
        const emailConductor = conductoresActivosList[0][0];
        const conductor = conductoresUnicos[emailConductor];
        if (conductor) {
          const nombreCompleto = `${conductor.nombre} ${conductor.apellidos}`.trim();
          filtros.push(nombreCompleto || conductor.email);
        }
      } else if (conductoresActivosList.length > 1) {
        filtros.push(`Conductores: ${conductoresActivosList.length}`);
      }
    }

    if (idiomasTotal > 0) {
      if (idiomasActivosList.length === idiomasTotal) {
        filtros.push('Idiomas: todos');
      } else if (idiomasActivosList.length === 1) {
        const idioma = idiomasActivosList[0][0];
        const banderaPath = getBanderaPath(idioma);
        return (
          <>
            {filtros.length > 0 && ` (${filtros.join(', ')}, `}
            <Image
              src={banderaPath}
              alt={idioma}
              boxSize="16px"
              objectFit="contain"
              display="inline-block"
              mx={1}
            />
            {')'}
          </>
        );
      } else if (idiomasActivosList.length > 1) {
        filtros.push(`Idiomas: ${idiomasActivosList.length}`);
      }
    }

    if (filtros.length > 0) {
      return ` (${filtros.join(', ')})`;
    }
    return null;
  };

  return (
    <VStack spacing={4} align="stretch" p={4} bg="black" color="white" minH="100vh" className="admin-page-container">
      <Box display="flex" justifyContent="space-between" alignItems="center" className="admin-page-header">
        <Text fontSize="xl" fontWeight="bold" className="admin-page-title">
          {t('menu_admin_stats')}
        </Text>
      </Box>

      {/* Botones para mostrar/ocultar filtros y selectores */}
      <HStack spacing={4} flexWrap="wrap">
        <Button
          size="sm"
          onClick={toggleTodosFiltros}
          className="admin-button-primary"
          colorScheme="purple"
          variant="solid"
        >
          {(mostrarSelectores && mostrarFiltros && mostrarFiltrosClientes && mostrarFiltrosConductores && mostrarFiltrosIdiomas) ? '📂' : '📁'}
          {' '}
          {(mostrarSelectores && mostrarFiltros && mostrarFiltrosClientes && mostrarFiltrosConductores && mostrarFiltrosIdiomas) ? 'Colapsar todos' : 'Expandir todos'}
        </Button>
        <Divider orientation="vertical" h="32px" borderColor="gray.600" />
        <Button
          size="sm"
          onClick={() => setMostrarSelectores(!mostrarSelectores)}
          className="admin-button-primary"
        >
          📅 {mostrarSelectores ? '▲' : '▼'} Selectores temporales
        </Button>
        <Button
          size="sm"
          onClick={() => setMostrarFiltros(!mostrarFiltros)}
          className="admin-button-primary"
        >
          🔍 {mostrarFiltros ? '▲' : '▼'} Filtros de estados
        </Button>
        <Button
          size="sm"
          onClick={() => setMostrarFiltrosClientes(!mostrarFiltrosClientes)}
          className="admin-button-primary"
        >
          👥 {mostrarFiltrosClientes ? '▲' : '▼'} Filtros de clientes
        </Button>
        <Button
          size="sm"
          onClick={() => setMostrarFiltrosConductores(!mostrarFiltrosConductores)}
          className="admin-button-primary"
        >
          🚗 {mostrarFiltrosConductores ? '▲' : '▼'} Filtros de conductores
        </Button>
        <Button
          size="sm"
          onClick={() => setMostrarFiltrosIdiomas(!mostrarFiltrosIdiomas)}
          className="admin-button-primary"
        >
          🌍 {mostrarFiltrosIdiomas ? '▲' : '▼'} Idiomas de la reserva
        </Button>
      </HStack>

      {/* Quíntuple columna: Selectores, Filtros de estados, Filtros de clientes, Filtros de conductores e Idiomas */}
      {(mostrarFiltros || mostrarSelectores || mostrarFiltrosClientes || mostrarFiltrosConductores || mostrarFiltrosIdiomas) && (
        <SimpleGrid columns={5} spacing={4}>
          {/* Columna izquierda: Selectores temporales */}
          {mostrarSelectores && (
            <Box className="filter-section">
              <Box mb={4}>
                <FormLabel color="black" fontSize="sm" mb={2}>
                  Fecha de fin
                </FormLabel>
                <Input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  bg="white"
                  color="gray.800"
                  size="sm"
                  maxW="200px"
                  max={new Date().toISOString().split('T')[0]}
                />
              </Box>

              <Text className="filter-section-title">
                Periodos Temporales
              </Text>
              <Box className="filter-container">
                <FilterBadge
                  colorScheme="blue"
                  isActive={periodoActivo === '24horas'}
                  onClick={() => setPeriodoActivo('24horas')}
                  label="24 horas"
                />
                <FilterBadge
                  colorScheme="yellow"
                  isActive={periodoActivo === 'semana'}
                  onClick={() => setPeriodoActivo('semana')}
                  label="Semana"
                />
                <FilterBadge
                  colorScheme="green"
                  isActive={periodoActivo === 'mes'}
                  onClick={() => setPeriodoActivo('mes')}
                  label="Mes"
                />
                <FilterBadge
                  colorScheme="orange"
                  isActive={periodoActivo === 'trimestre'}
                  onClick={() => setPeriodoActivo('trimestre')}
                  label="Trimestre"
                />
                <FilterBadge
                  colorScheme="blue"
                  isActive={periodoActivo === 'anio'}
                  onClick={() => setPeriodoActivo('anio')}
                  label="Año"
                />
                <FilterBadge
                  colorScheme="green"
                  isActive={periodoActivo === 'todos'}
                  onClick={() => setPeriodoActivo('todos')}
                  label="Todos los datos"
                />
              </Box>

              {periodoActivo !== 'todos' && (
                <Box mt={4}>
                  <FormLabel color="black" fontSize="sm" mb={2}>
                    Fecha de inicio
                  </FormLabel>
                  <Input
                    type="date"
                    value={fechaInicio}
                    bg="gray.100"
                    color="gray.800"
                    size="sm"
                    maxW="200px"
                    isReadOnly
                    cursor="not-allowed"
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Columna central: Filtros de estados */}
          {mostrarFiltros && (
            <Box className="filter-section">
              <ReservaFilters
                estadosActivos={estadosActivos}
                subEstadosActivos={subEstadosActivos}
                toggleEstado={toggleEstado}
                toggleSubEstado={toggleSubEstado}
              />
            </Box>
          )}

          {/* Columna derecha: Filtros de clientes */}
          {mostrarFiltrosClientes && (
            <Box className="filter-section" bg="white" p={4} borderRadius="md">
              <HStack justifyContent="space-between" mb={3}>
                <Text fontSize="lg" fontWeight="bold" color="gray.700">
                  Filtros de Clientes
                </Text>
                <Button
                  size="xs"
                  onClick={() => {
                    const todosActivos = Object.values(clientesActivos).every(v => v);
                    const nuevoEstado = {};
                    Object.keys(clientesActivos).forEach(key => {
                      nuevoEstado[key] = !todosActivos;
                    });
                    setClientesActivos(nuevoEstado);
                  }}
                  variant="ghost"
                  colorScheme="teal"
                  title={Object.values(clientesActivos).every(v => v) ? "Deseleccionar todos" : "Seleccionar todos"}
                >
                  {Object.values(clientesActivos).every(v => v) ? '☒' : '☐'}
                </Button>
              </HStack>
              <Wrap spacing={2}>
                {Object.entries(clientesUnicos).map(([email, cliente], index) => {
                  const colorScheme = CLIENTE_COLORS[index % CLIENTE_COLORS.length];
                  const isActive = clientesActivos[email];
                  const displayText = `${cliente.nombre} ${cliente.apellidos} (${cliente.email})`.trim();
                  
                  return (
                    <WrapItem key={email}>
                      <Badge
                        colorScheme={colorScheme}
                        variant={isActive ? 'solid' : 'outline'}
                        px={3}
                        py={1}
                        borderRadius="full"
                        cursor="pointer"
                        onClick={() => toggleCliente(email)}
                        fontSize="xs"
                        opacity={isActive ? 1 : 0.5}
                        _hover={{ opacity: 0.8 }}
                        whiteSpace="normal"
                        wordBreak="break-word"
                        textAlign="center"
                        maxW="200px"
                      >
                        {displayText || email}
                      </Badge>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </Box>
          )}
          
          {/* Cuarta columna: Filtros de conductores */}
          {mostrarFiltrosConductores && (
            <Box className="filter-section" bg="white" p={4} borderRadius="md">
              <HStack justifyContent="space-between" mb={3}>
                <Text fontSize="lg" fontWeight="bold" color="gray.700">
                  Filtros de Conductores
                </Text>
                <Button
                  size="xs"
                  onClick={() => {
                    const todosActivos = Object.values(conductoresActivos).every(v => v);
                    const nuevoEstado = {};
                    Object.keys(conductoresActivos).forEach(key => {
                      nuevoEstado[key] = !todosActivos;
                    });
                    setConductoresActivos(nuevoEstado);
                  }}
                  variant="ghost"
                  colorScheme="teal"
                  title={Object.values(conductoresActivos).every(v => v) ? "Deseleccionar todos" : "Seleccionar todos"}
                >
                  {Object.values(conductoresActivos).every(v => v) ? '☒' : '☐'}
                </Button>
              </HStack>
              <Wrap spacing={2}>
                {Object.entries(conductoresUnicos).map(([email, conductor], index) => {
                  const colorScheme = CLIENTE_COLORS[index % CLIENTE_COLORS.length];
                  const isActive = conductoresActivos[email];
                  const displayText = `${conductor.nombre} ${conductor.apellidos} (${conductor.email})`.trim();
                  
                  return (
                    <WrapItem key={email}>
                      <Badge
                        colorScheme={colorScheme}
                        variant={isActive ? 'solid' : 'outline'}
                        px={3}
                        py={1}
                        borderRadius="full"
                        cursor="pointer"
                        onClick={() => toggleConductor(email)}
                        fontSize="xs"
                        opacity={isActive ? 1 : 0.5}
                        _hover={{ opacity: 0.8 }}
                        whiteSpace="normal"
                        wordBreak="break-word"
                        textAlign="center"
                        maxW="200px"
                      >
                        {displayText || email}
                      </Badge>
                    </WrapItem>
                  );
                })}
              </Wrap>
            </Box>
          )}
          
          {/* Quinta columna: Filtros de idiomas */}
          {mostrarFiltrosIdiomas && (
            <Box className="filter-section" bg="white" p={4} borderRadius="md">
              <HStack justifyContent="space-between" mb={3}>
                <Text fontSize="lg" fontWeight="bold" color="gray.700">
                  Idiomas de la Reserva
                </Text>
                <Button
                  size="xs"
                  onClick={() => {
                    const todosActivos = Object.values(idiomasActivos).every(v => v);
                    const nuevoEstado = {};
                    Object.keys(idiomasActivos).forEach(key => {
                      nuevoEstado[key] = !todosActivos;
                    });
                    setIdiomasActivos(nuevoEstado);
                  }}
                  variant="ghost"
                  colorScheme="teal"
                  title={Object.values(idiomasActivos).every(v => v) ? "Deseleccionar todos" : "Seleccionar todos"}
                >
                  {Object.values(idiomasActivos).every(v => v) ? '☒' : '☐'}
                </Button>
              </HStack>
              {idiomasUnicos.length === 0 ? (
                <Text color="gray.500" fontSize="sm">
                  No se encontraron idiomas en las reservas
                </Text>
              ) : (
                <Wrap spacing={2}>
                  {idiomasUnicos.map((idioma, index) => {
                    const colorScheme = CLIENTE_COLORS[index % CLIENTE_COLORS.length];
                    const isActive = idiomasActivos[idioma];
                    const banderaPath = getBanderaPath(idioma);
                    
                    return (
                      <WrapItem key={idioma}>
                        <Badge
                          colorScheme={colorScheme}
                          variant={isActive ? 'solid' : 'outline'}
                          px={3}
                          py={1}
                          borderRadius="full"
                          cursor="pointer"
                          onClick={() => toggleIdioma(idioma)}
                          fontSize="sm"
                          opacity={isActive ? 1 : 0.5}
                          _hover={{ opacity: 0.8 }}
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                          <Image 
                            src={banderaPath} 
                            alt={idioma} 
                            boxSize="16px" 
                            objectFit="contain"
                          />
                          {idioma}
                        </Badge>
                      </WrapItem>
                    );
                  })}
                </Wrap>
              )}
            </Box>
          )}
        </SimpleGrid>
      )}

      {/* Card Reservas */}
      <Card variant="outline" borderColor="gray.700" bg="white">
        <CardHeader 
          bg="blue.100" 
          color="gray.800" 
          borderTopRadius="md"
          cursor="pointer"
          onClick={() => toggleCard('reservas')}
          _hover={{ bg: 'blue.200' }}
        >
          <HStack justify="space-between">
            <Text fontSize="lg" fontWeight="bold">
              📋 Estadísticas de Reservas
              {renderResumenFiltros()}
            </Text>
            <Text fontSize="2xl">{expandedCards.reservas ? '▲' : '▼'}</Text>
          </HStack>
        </CardHeader>
        {expandedCards.reservas && (
          <CardBody>
            {loading ? (
              <VStack spacing={4}>
                <Spinner size="xl" color="blue.500" />
                <Text color="gray.600">Cargando estadísticas...</Text>
              </VStack>
            ) : error ? (
              <Text color="red.500">Error: {error}</Text>
            ) : (
              <VStack spacing={6} align="stretch">
                {/* KPIs principales */}
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <Box bg="blue.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                    <Stat>
                      <StatLabel color="gray.600" fontSize="sm">Total Reservas</StatLabel>
                      <StatNumber color="blue.600" fontSize="3xl">{stats.total}</StatNumber>
                      <StatHelpText color="gray.500">En periodo seleccionado</StatHelpText>
                    </Stat>
                  </Box>
                  
                  <Box bg="green.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                    <Stat>
                      <StatLabel color="gray.600" fontSize="sm">Ingresos Totales</StatLabel>
                      <StatNumber color="green.600" fontSize="3xl">€{stats.ingresosTotales.toFixed(2)}</StatNumber>
                      <StatHelpText color="gray.500">Suma de reservas</StatHelpText>
                    </Stat>
                  </Box>
                  
                  <Box bg="orange.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                    <Stat>
                      <StatLabel color="gray.600" fontSize="sm">Adjudicaciones</StatLabel>
                      <StatNumber color="orange.600" fontSize="3xl">€{stats.ingresosAdjudicacion.toFixed(2)}</StatNumber>
                      <StatHelpText color="gray.500">Cobros conductores</StatHelpText>
                    </Stat>
                  </Box>
                  
                  <Box bg="purple.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                    <Stat>
                      <StatLabel color="gray.600" fontSize="sm">Promedio/Reserva</StatLabel>
                      <StatNumber color="purple.600" fontSize="3xl">€{stats.promedioReserva.toFixed(2)}</StatNumber>
                      <StatHelpText color="gray.500">Ticket medio</StatHelpText>
                    </Stat>
                  </Box>
                </SimpleGrid>

                <Divider />

                {/* Gráficos de distribución */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                  {/* Gráfico de estados */}
                  <Box>
                    <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                      Distribución por Estado
                    </Text>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={dataEstados}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {dataEstados.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Gráfico de idiomas */}
                  <Box>
                    <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                      Distribución por Idioma
                    </Text>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={dataIdiomas}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {dataIdiomas.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Gráfico de tipo de servicio */}
                  <Box>
                    <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                      Tipo de Servicio
                    </Text>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={dataTipoServicio}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#82ca9d"
                          dataKey="value"
                        >
                          {dataTipoServicio.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={index === 0 ? '#FF8042' : '#00C49F'} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Gráfico de tipo de dispositivo */}
                  <Box>
                    <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                      Por dispositivo de cliente
                    </Text>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={dataTipoDispositivo}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {dataTipoDispositivo.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </SimpleGrid>

                <Divider />

                {/* Gráfico de evolución temporal */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                    Cantidad de reservas por días ({fechaInicio || 'inicio'} - {fechaFin})
                    {(() => {
                      const clientesActivosList = Object.entries(clientesActivos).filter(([_, activo]) => activo);
                      const conductoresActivosList = Object.entries(conductoresActivos).filter(([_, activo]) => activo);
                      const idiomasActivosList = Object.entries(idiomasActivos).filter(([_, activo]) => activo);
                      
                      const extras = [];
                      
                      // Si solo hay un cliente seleccionado
                      if (clientesActivosList.length === 1) {
                        const emailCliente = clientesActivosList[0][0];
                        const cliente = clientesUnicos[emailCliente];
                        if (cliente) {
                          const nombreCompleto = `${cliente.nombre} ${cliente.apellidos}`.trim();
                          if (nombreCompleto) {
                            extras.push(nombreCompleto);
                          }
                        }
                      }
                      
                      // Si solo hay un conductor seleccionado
                      if (conductoresActivosList.length === 1) {
                        const emailConductor = conductoresActivosList[0][0];
                        const conductor = conductoresUnicos[emailConductor];
                        if (conductor) {
                          const nombreCompleto = `${conductor.nombre} ${conductor.apellidos}`.trim();
                          if (nombreCompleto) {
                            extras.push(nombreCompleto);
                          }
                        }
                      }
                      
                      // Si solo hay un idioma seleccionado
                      if (idiomasActivosList.length === 1) {
                        const idioma = idiomasActivosList[0][0];
                        const banderaPath = getBanderaPath(idioma);
                        extras.push(
                          <Image 
                            key="idioma"
                            src={banderaPath} 
                            alt={idioma} 
                            boxSize="20px" 
                            objectFit="contain"
                            display="inline-block"
                            ml={1}
                          />
                        );
                      }
                      
                      if (extras.length > 0) {
                        return (
                          <>
                            {' - '}
                            {extras.map((extra, index) => (
                              <React.Fragment key={index}>
                                {index > 0 && ', '}
                                {extra}
                              </React.Fragment>
                            ))}
                          </>
                        );
                      }
                      return null;
                    })()}
                  </Text>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.reservasPorDia}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="fecha" 
                        angle={stats.reservasPorDia.length > 30 ? -45 : 0}
                        textAnchor={stats.reservasPorDia.length > 30 ? "end" : "middle"}
                        height={stats.reservasPorDia.length > 30 ? 80 : 60}
                        interval={stats.reservasPorDia.length > 90 ? Math.floor(stats.reservasPorDia.length / 15) : 'preserveStartEnd'}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="cantidad" stroke="#8884d8" strokeWidth={2} name="Reservas" />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>

                {/* Gráfico de rangos de precio */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                    Cantidad de reservas por precio aproximado ({fechaInicio || 'inicio'} - {fechaFin})
                    {(() => {
                      const clientesActivosList = Object.entries(clientesActivos).filter(([_, activo]) => activo);
                      const conductoresActivosList = Object.entries(conductoresActivos).filter(([_, activo]) => activo);
                      const idiomasActivosList = Object.entries(idiomasActivos).filter(([_, activo]) => activo);
                      
                      const extras = [];
                      
                      // Si solo hay un cliente seleccionado
                      if (clientesActivosList.length === 1) {
                        const emailCliente = clientesActivosList[0][0];
                        const cliente = clientesUnicos[emailCliente];
                        if (cliente) {
                          const nombreCompleto = `${cliente.nombre} ${cliente.apellidos}`.trim();
                          if (nombreCompleto) {
                            extras.push(nombreCompleto);
                          }
                        }
                      }
                      
                      // Si solo hay un conductor seleccionado
                      if (conductoresActivosList.length === 1) {
                        const emailConductor = conductoresActivosList[0][0];
                        const conductor = conductoresUnicos[emailConductor];
                        if (conductor) {
                          const nombreCompleto = `${conductor.nombre} ${conductor.apellidos}`.trim();
                          if (nombreCompleto) {
                            extras.push(nombreCompleto);
                          }
                        }
                      }
                      
                      // Si solo hay un idioma seleccionado
                      if (idiomasActivosList.length === 1) {
                        const idioma = idiomasActivosList[0][0];
                        const banderaPath = getBanderaPath(idioma);
                        extras.push(
                          <Image 
                            key="idioma"
                            src={banderaPath} 
                            alt={idioma} 
                            boxSize="20px" 
                            objectFit="contain"
                            display="inline-block"
                            ml={1}
                          />
                        );
                      }
                      
                      if (extras.length > 0) {
                        return (
                          <>
                            {' - '}
                            {extras.map((extra, index) => (
                              <React.Fragment key={index}>
                                {index > 0 && ', '}
                                {extra}
                              </React.Fragment>
                            ))}
                          </>
                        );
                      }
                      return null;
                    })()}
                  </Text>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.reservasPorPrecio}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="rango" 
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="cantidad" fill="#82ca9d" name="Reservas" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Gráfico de códigos postales */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                    Origen por código postal ({fechaInicio || 'inicio'} - {fechaFin})
                    {(() => {
                      const clientesActivosList = Object.entries(clientesActivos).filter(([_, activo]) => activo);
                      const conductoresActivosList = Object.entries(conductoresActivos).filter(([_, activo]) => activo);
                      const idiomasActivosList = Object.entries(idiomasActivos).filter(([_, activo]) => activo);
                      
                      const extras = [];
                      
                      // Si solo hay un cliente seleccionado
                      if (clientesActivosList.length === 1) {
                        const emailCliente = clientesActivosList[0][0];
                        const cliente = clientesUnicos[emailCliente];
                        if (cliente) {
                          const nombreCompleto = `${cliente.nombre} ${cliente.apellidos}`.trim();
                          if (nombreCompleto) {
                            extras.push(nombreCompleto);
                          }
                        }
                      }
                      
                      // Si solo hay un conductor seleccionado
                      if (conductoresActivosList.length === 1) {
                        const emailConductor = conductoresActivosList[0][0];
                        const conductor = conductoresUnicos[emailConductor];
                        if (conductor) {
                          const nombreCompleto = `${conductor.nombre} ${conductor.apellidos}`.trim();
                          if (nombreCompleto) {
                            extras.push(nombreCompleto);
                          }
                        }
                      }
                      
                      // Si solo hay un idioma seleccionado
                      if (idiomasActivosList.length === 1) {
                        const idioma = idiomasActivosList[0][0];
                        const banderaPath = getBanderaPath(idioma);
                        extras.push(
                          <Image 
                            key="idioma"
                            src={banderaPath} 
                            alt={idioma} 
                            boxSize="20px" 
                            objectFit="contain"
                            display="inline-block"
                            ml={1}
                          />
                        );
                      }
                      
                      if (extras.length > 0) {
                        return (
                          <>
                            {' - '}
                            {extras.map((extra, index) => (
                              <React.Fragment key={index}>
                                {index > 0 && ', '}
                                {extra}
                              </React.Fragment>
                            ))}
                          </>
                        );
                      }
                      return null;
                    })()}
                  </Text>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.reservasPorCodigoPostal}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="codigo" 
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="cantidad" fill="#8884d8" name="Reservas" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Gráfico de códigos postales de destino */}
                <Box>
                  <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                    Destino por código postal ({fechaInicio || 'inicio'} - {fechaFin})
                    {(() => {
                      const clientesActivosList = Object.entries(clientesActivos).filter(([_, activo]) => activo);
                      const conductoresActivosList = Object.entries(conductoresActivos).filter(([_, activo]) => activo);
                      const idiomasActivosList = Object.entries(idiomasActivos).filter(([_, activo]) => activo);
                      
                      const extras = [];
                      
                      // Si solo hay un cliente seleccionado
                      if (clientesActivosList.length === 1) {
                        const emailCliente = clientesActivosList[0][0];
                        const cliente = clientesUnicos[emailCliente];
                        if (cliente) {
                          const nombreCompleto = `${cliente.nombre} ${cliente.apellidos}`.trim();
                          if (nombreCompleto) {
                            extras.push(nombreCompleto);
                          }
                        }
                      }
                      
                      // Si solo hay un conductor seleccionado
                      if (conductoresActivosList.length === 1) {
                        const emailConductor = conductoresActivosList[0][0];
                        const conductor = conductoresUnicos[emailConductor];
                        if (conductor) {
                          const nombreCompleto = `${conductor.nombre} ${conductor.apellidos}`.trim();
                          if (nombreCompleto) {
                            extras.push(nombreCompleto);
                          }
                        }
                      }
                      
                      // Si solo hay un idioma seleccionado
                      if (idiomasActivosList.length === 1) {
                        const idioma = idiomasActivosList[0][0];
                        const banderaPath = getBanderaPath(idioma);
                        extras.push(
                          <Image 
                            key="idioma"
                            src={banderaPath} 
                            alt={idioma} 
                            boxSize="20px" 
                            objectFit="contain"
                            display="inline-block"
                            ml={1}
                          />
                        );
                      }
                      
                      if (extras.length > 0) {
                        return (
                          <>
                            {' - '}
                            {extras.map((extra, index) => (
                              <React.Fragment key={index}>
                                {index > 0 && ', '}
                                {extra}
                              </React.Fragment>
                            ))}
                          </>
                        );
                      }
                      return null;
                    })()}
                  </Text>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.reservasPorCodigoPostalDestino}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="codigo" 
                        angle={-45}
                        textAnchor="end"
                        height={100}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="cantidad" fill="#FF8042" name="Reservas" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

              </VStack>
            )}
          </CardBody>
        )}
      </Card>

      {/* Card Clientes */}
      <Card variant="outline" borderColor="gray.700" bg="white">
        <CardHeader 
          bg="green.100" 
          color="gray.800" 
          borderTopRadius="md"
          cursor="pointer"
          onClick={() => toggleCard('clientes')}
          _hover={{ bg: 'green.200' }}
        >
          <HStack justify="space-between">
            <Text fontSize="lg" fontWeight="bold">
              👥 Estadísticas de Clientes
              {renderResumenFiltros()}
            </Text>
            <Text fontSize="2xl">{expandedCards.clientes ? '▲' : '▼'}</Text>
          </HStack>
        </CardHeader>
        {expandedCards.clientes && (
          <CardBody>
            <VStack spacing={6} align="stretch">
              {/* KPIs de clientes */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box bg="green.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                  <Stat>
                    <StatLabel color="gray.600" fontSize="sm">Total Clientes</StatLabel>
                    <StatNumber color="green.600" fontSize="3xl">{stats.totalClientes}</StatNumber>
                    <StatHelpText color="gray.500">Clientes únicos</StatHelpText>
                  </Stat>
                </Box>
                
                <Box bg="blue.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                  <Stat>
                    <StatLabel color="gray.600" fontSize="sm">Reservas/Cliente</StatLabel>
                    <StatNumber color="blue.600" fontSize="3xl">{stats.reservasPorCliente}</StatNumber>
                    <StatHelpText color="gray.500">Promedio</StatHelpText>
                  </Stat>
                </Box>
                
                <Box bg="purple.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                  <Stat>
                    <StatLabel color="gray.600" fontSize="sm">Total Reservas</StatLabel>
                    <StatNumber color="purple.600" fontSize="3xl">{stats.total}</StatNumber>
                    <StatHelpText color="gray.500">Del periodo</StatHelpText>
                  </Stat>
                </Box>
              </SimpleGrid>

              <Divider />

              {/* Top 5 clientes */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                  Top 5 Clientes con más reservas
                </Text>
                {stats.topClientes.length > 0 ? (
                  <VStack align="stretch" spacing={2}>
                    {stats.topClientes.map((cliente, index) => (
                      <Box 
                        key={cliente.email} 
                        p={3} 
                        bg="gray.50" 
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <HStack justify="space-between">
                          <HStack>
                            <Badge colorScheme="green" fontSize="md">{index + 1}</Badge>
                            <Text fontWeight="bold" color="gray.700">
                              {cliente.nombre} {cliente.apellidos}
                            </Text>
                            <Text fontSize="sm" color="gray.500">({cliente.email})</Text>
                          </HStack>
                          <Badge colorScheme="blue" fontSize="lg">{cliente.count} reservas</Badge>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Text color="gray.500">No hay datos de clientes</Text>
                )}
              </Box>
            </VStack>
          </CardBody>
        )}
      </Card>

      {/* Card Conductores */}
      <Card variant="outline" borderColor="gray.700" bg="white">
        <CardHeader 
          bg="orange.100" 
          color="gray.800" 
          borderTopRadius="md"
          cursor="pointer"
          onClick={() => toggleCard('conductores')}
          _hover={{ bg: 'orange.200' }}
        >
          <HStack justify="space-between">
            <Text fontSize="lg" fontWeight="bold">
              🚕 Estadísticas de Conductores
              {renderResumenFiltros()}
            </Text>
            <Text fontSize="2xl">{expandedCards.conductores ? '▲' : '▼'}</Text>
          </HStack>
        </CardHeader>
        {expandedCards.conductores && (
          <CardBody>
            <VStack spacing={6} align="stretch">
              {/* KPIs de conductores */}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box bg="orange.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                  <Stat>
                    <StatLabel color="gray.600" fontSize="sm">Total Conductores</StatLabel>
                    <StatNumber color="orange.600" fontSize="3xl">{stats.totalConductores}</StatNumber>
                    <StatHelpText color="gray.500">Conductor con reservas</StatHelpText>
                  </Stat>
                </Box>

                <Box bg="blue.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                  <Stat>
                    <StatLabel color="gray.600" fontSize="sm">Reservas/Conductor</StatLabel>
                    <StatNumber color="blue.600" fontSize="3xl">{stats.reservasPorConductor}</StatNumber>
                    <StatHelpText color="gray.500">Promedio</StatHelpText>
                  </Stat>
                </Box>

                <Box bg="purple.50" p={4} borderRadius="md" textAlign="center" border="1px solid" borderColor="gray.700" boxShadow="2px 2px 4px rgba(0,0,0,0.2)">
                  <Stat>
                    <StatLabel color="gray.600" fontSize="sm">Reservas con conductor</StatLabel>
                    <StatNumber color="purple.600" fontSize="3xl">{stats.reservasConConductor}</StatNumber>
                    <StatHelpText color="gray.500">Del periodo</StatHelpText>
                  </Stat>
                </Box>
              </SimpleGrid>

              <Divider />

              {/* Top 5 conductores */}
              <Box>
                <Text fontSize="lg" fontWeight="bold" color="gray.700" mb={3}>
                  Top 5 Conductores con más reservas
                </Text>
                {stats.topConductores.length > 0 ? (
                  <VStack align="stretch" spacing={2}>
                    {stats.topConductores.map((conductor, index) => (
                      <Box
                        key={conductor.email}
                        p={3}
                        bg="gray.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <HStack justify="space-between">
                          <HStack>
                            <Badge colorScheme="orange" fontSize="md">{index + 1}</Badge>
                            <Text fontWeight="bold" color="gray.700">
                              {conductor.nombre} {conductor.apellidos}
                            </Text>
                            <Text fontSize="sm" color="gray.500">({conductor.email})</Text>
                          </HStack>
                          <Badge colorScheme="blue" fontSize="lg">{conductor.count} reservas</Badge>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Text color="gray.500">No hay datos de conductores</Text>
                )}
              </Box>
            </VStack>
          </CardBody>
        )}
      </Card>
    </VStack>
  );
};

export default AdminStats;
